import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, Card, EmptyState, ErrorState, Input, Loading } from '@/components/ui';
import { formatDateTime } from '@/lib/format';

/**
 * Every channel Asterisk handed over, and what became of it.
 *
 * Built for one report that kept coming back and could not be answered:
 * Vicidial shows the calls, `core show channels` shows the calls, and the
 * platform shows none. Every screen here reads the `calls` table, which by
 * definition holds the calls that got far enough to become rows — so a call
 * refused for want of a bot, or for an unresolvable tenant, or one whose media
 * never opened, was invisible on every one of them.
 *
 * The rows with no call id are the point of this page.
 */

interface Arrival {
  arrived_at: string;
  number: string | null;
  channel_id: string | null;
  campaign: string | null;
  lead_id: string | null;
  vicidial_user: string | null;
  call_id: string | null;
  state: string | null;
  disposition: string | null;
  ended_at: string | null;
  duration_sec: number | null;
  client_name: string | null;
  outcome: string;
  stasis_args: Record<string, string> | null;
}

interface Arrivals {
  window_hours: number;
  data: Arrival[];
  arrived: number;
  lost_before_a_call_row: number;
  hint: string;
}

export default function CallArrivals({ hours }: { hours: number }) {
  const [number, setNumber] = useState('');
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  // Debounced by the user pressing keys slowly enough; the query key changing
  // on every character is fine at this row count and avoids a stale-closure
  // debounce that would need testing of its own.
  const arrivals = useQuery({
    queryKey: ['call-arrivals', hours, number, onlyFailed],
    queryFn: () =>
      api.get<Arrivals>('/admin/call-arrivals', {
        hours,
        ...(number ? { number } : {}),
        ...(onlyFailed ? { only_failed: true } : {}),
      }),
  });

  if (arrivals.isPending) return <Loading />;
  if (arrivals.error)
    return <ErrorState error={arrivals.error} retry={() => arrivals.refetch()} />;

  const { data, arrived, lost_before_a_call_row: lost, hint } = arrivals.data;

  return (
    <Card
      title="Calls arriving from Asterisk"
      description="One row per channel handed to the bot, including the ones that never became calls."
      padded={false}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Search a phone number"
            className="w-48"
          />
          <label className="flex select-none items-center gap-1.5 text-xs text-ink-600 dark:text-ink-300">
            <input
              type="checkbox"
              checked={onlyFailed}
              onChange={(e) => setOnlyFailed(e.target.checked)}
              className="h-3.5 w-3.5 accent-brand-600"
            />
            Only the lost ones
          </label>
        </div>
      }
    >
      {/* The headline is a sentence, not two numbers to compare by eye. */}
      <div className="border-b border-line px-4 py-3 text-sm">
        {lost ? (
          <span className="text-amber-700 dark:text-amber-400">
            <strong className="tabular-nums">{arrived}</strong> channels arrived in the
            last {hours}h and{' '}
            <strong className="tabular-nums">{lost}</strong> never became calls on this
            platform.
          </span>
        ) : (
          <span className="text-ink-600 dark:text-ink-300">
            <strong className="tabular-nums">{arrived}</strong> channels arrived in the
            last {hours}h and every one became a call.
          </span>
        )}
      </div>

      {!data.length ? (
        <EmptyState
          title="Nothing arrived"
          // The empty state carries the diagnosis, because an empty list here
          // IS the finding when the dialer says otherwise.
          description={hint}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="glee-table">
            <thead>
              <tr>
                <th>Arrived</th>
                <th>Number</th>
                <th>Campaign</th>
                <th>Client</th>
                <th>Outcome</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((row) => {
                const key = `${row.channel_id}-${row.arrived_at}`;
                const lostRow = !row.call_id;
                return (
                  <Fragment key={key}>
                    <tr
                      className={
                        lostRow ? 'bg-amber-50/60 dark:bg-amber-950/20' : undefined
                      }
                    >
                      <td className="whitespace-nowrap text-xs text-ink-500">
                        {formatDateTime(row.arrived_at)}
                      </td>
                      <td className="whitespace-nowrap font-medium tabular-nums">
                        {row.number ?? '—'}
                      </td>
                      <td className="whitespace-nowrap text-ink-600 dark:text-ink-300">
                        {row.campaign ?? '—'}
                      </td>
                      <td className="whitespace-nowrap text-ink-600 dark:text-ink-300">
                        {row.client_name ?? '—'}
                      </td>
                      <td>
                        <Badge tone={lostRow ? 'amber' : 'gray'}>{row.outcome}</Badge>
                      </td>
                      <td className="text-right">
                        <button
                          type="button"
                          onClick={() => setOpen(open === key ? null : key)}
                          className="text-xs text-brand-600 hover:underline dark:text-brand-400"
                        >
                          {open === key ? 'hide' : 'dialplan'}
                        </button>
                      </td>
                    </tr>
                    {open === key && (
                      <tr>
                        <td colSpan={6} className="bg-sunken py-3">
                          {/* Verbatim. When the dialplan is wrong the answer is
                              in here, and a tidied summary is exactly the part
                              that would have been edited out. */}
                          <pre className="overflow-x-auto text-2xs text-ink-600 dark:text-ink-300">
                            {JSON.stringify(row.stasis_args ?? {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
